import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid2 as Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  MedicalServices as ProfessionalIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  EventRepeat as RescheduleIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Badge as BadgeIcon,
  AdminPanelSettings as AdminIcon,
  Public as PublicIcon,
  AccessTime as TimeIcon,
  Event as DateIcon,
  Timer as DurationIcon,
  Notes as NotesIcon,
  Sell as PriceIcon,
  Notifications as NotificationIcon,
  DoneAll as ReadIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { formatDateTime, formatTime, formatDuration, formatCurrency } from '../utils/formatters';
import { getAppointmentStatusStyles, getAppointmentStatusLabel } from '../constants/statusConfig';
import PhoneDisplay from './ui/PhoneDisplay';
import PersonDetailDialog from './persons/PersonDetailDialog';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStatus(details) {
  if (!details) return 'pending';
  if (details.cancelledAt) return 'cancelled';
  if (details.attendedAt) return 'completed';
  const aptTime = new Date(details.startTime);
  if (new Date() > aptTime && details.confirmedAt) return 'no_show';
  if (details.confirmedAt) return 'confirmed';
  return 'pending';
}

// Label para eventos de histórico do appointment
function historyActionLabel(action) {
  const map = {
    Created: 'Agendamento criado',
    StatusChanged: 'Status alterado',
    Confirmed: 'Confirmado',
    Cancelled: 'Cancelado',
    Attended: 'Marcado como atendido',
    Rescheduled: 'Reagendado',
    Updated: 'Dados atualizados',
    NoShow: 'Não compareceu',
  };
  return map[action] ?? action;
}

// Label para notificações
function notificationTypeLabel(type) {
  const map = {
    'appointment.created.public': 'Notificação: Novo agendamento (site)',
    'appointment.created': 'Notificação: Agendamento criado (admin)',
    'appointment.cancelled': 'Notificação: Agendamento cancelado',
    'appointment.reminder': 'Lembrete enviado',
  };
  return map[type] ?? `Notificação: ${type}`;
}

function historyActionColor(action, source) {
  if (source === 'notification') return 'secondary';
  if (action === 'Created') return 'primary';
  if (action === 'Confirmed') return 'success';
  if (action === 'Cancelled') return 'error';
  if (action === 'Attended') return 'info';
  if (action === 'NoShow') return 'warning';
  return 'default';
}

function buildAddress(place) {
  if (!place) return null;
  if (place.address) return place.address;
  const parts = [
    place.addressStreet && place.addressNumber
      ? `${place.addressStreet}, ${place.addressNumber}`
      : place.addressStreet,
    place.addressComplement,
    place.addressNeighborhood,
    place.addressCity && place.addressState
      ? `${place.addressCity} - ${place.addressState}`
      : place.addressCity,
    place.addressZipCode ? `CEP ${place.addressZipCode}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InfoRow({ icon, label, value, href }) {
  if (!value) return null;
  return (
    <Box display="flex" alignItems="flex-start" gap={1} mb={0.75}>
      <Box sx={{ color: 'text.secondary', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
      <Box>
        {label && (
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
        )}
        {href ? (
          <Typography
            variant="body2"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2">{value}</Typography>
        )}
      </Box>
    </Box>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={1.5} mt={2.5}>
      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight="bold" textTransform="uppercase" letterSpacing={0.5}>
        {title}
      </Typography>
    </Box>
  );
}

function HistoryTimeline({ history }) {
  if (!history?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhum evento registrado.
      </Typography>
    );
  }

  return (
    <Box>
      {history.map((item, idx) => {
        const isNotification = item.source === 'notification';
        const color = historyActionColor(item.action, item.source);
        const label = isNotification
          ? notificationTypeLabel(item.action)
          : historyActionLabel(item.action);

        return (
          <Box key={item.id ?? idx} display="flex" gap={1.5} mb={1.5}>
            {/* Dot + line */}
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ flexShrink: 0 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: `${color}.main`,
                  mt: 0.6,
                  flexShrink: 0,
                }}
              />
              {idx < history.length - 1 && (
                <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.5 }} />
              )}
            </Box>

            {/* Content */}
            <Box pb={idx < history.length - 1 ? 1.5 : 0} flex={1}>
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                {isNotification && (
                  <NotificationIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
                )}
                <Chip
                  label={label}
                  size="small"
                  color={color}
                  variant={isNotification ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(item.changedAt)}
                </Typography>
                {isNotification && item.readAt && (
                  <Tooltip title={`Lida em ${formatDateTime(item.readAt)}`}>
                    <ReadIcon sx={{ fontSize: 13, color: 'success.main' }} />
                  </Tooltip>
                )}
                {isNotification && !item.readAt && (
                  <Chip label="não lida" size="small" variant="outlined" color="warning"
                    sx={{ height: 16, fontSize: '0.65rem' }} />
                )}
              </Box>

              {item.changedBy && item.changedBy !== 'Notificação' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  por {item.changedBy}
                </Typography>
              )}

              {/* Para notificações: mostra a mensagem */}
              {isNotification && item.newValue && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.25, color: 'text.secondary', fontStyle: 'italic' }}
                >
                  {item.newValue}
                </Typography>
              )}

              {/* Para eventos de histórico: mostra old → new quando relevante */}
              {!isNotification && item.oldValue && item.newValue && (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 0.75,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" display="block" color="text.secondary">
                    <strong>Antes:</strong> {item.oldValue}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Depois:</strong> {item.newValue}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function AppointmentDetailsModal({
  appointmentId,
  open,
  onClose,
  canWrite = true,
  onConfirm,
  onMarkAttended,
  onCancel,
  onReschedule,
}) {
  const { data: details, isLoading, error } = useQuery({
    queryKey: ['appointmentDetails', appointmentId],
    queryFn: async () => {
      const res = await api.get(`/appointments/${appointmentId}/details`);
      return res.data;
    },
    enabled: open && !!appointmentId,
    staleTime: 0,
  });

  const [personOpen, setPersonOpen] = useState(false);

  const status = getStatus(details);
  const statusStyles = details ? getAppointmentStatusStyles(status) : null;
  const fullAddress = details ? buildAddress(details.place) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="h6" component="span" fontWeight="bold">
              {details?.clientName ?? 'Detalhes do Agendamento'}
            </Typography>
            {statusStyles && (
              <Chip
                label={getAppointmentStatusLabel(status)}
                size="small"
                color={statusStyles.chipColor}
              />
            )}
            {details?.bookingSource && (
              <Tooltip
                title={
                  details.bookingSource === 'admin'
                    ? `Criado pelo admin${details.createdByName ? ` (${details.createdByName})` : ''}`
                    : 'Agendado pelo cliente via link público'
                }
              >
                <Chip
                  icon={details.bookingSource === 'admin'
                    ? <AdminIcon sx={{ fontSize: 14 }} />
                    : <PublicIcon sx={{ fontSize: 14 }} />}
                  label={details.bookingSource === 'admin' ? 'Admin' : 'Público'}
                  size="small"
                  variant="outlined"
                  color={details.bookingSource === 'admin' ? 'secondary' : 'default'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Erro ao carregar detalhes do agendamento.
          </Alert>
        )}

        {details && (
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={3}>
              {/* ── Coluna Esquerda ── */}
              <Grid size={{ xs: 12, md: 6 }}>

                {/* AGENDAMENTO */}
                <SectionHeader icon={<DateIcon fontSize="small" />} title="Agendamento" />
                <InfoRow
                  icon={<TimeIcon fontSize="small" />}
                  label="Data e horário"
                  value={`${formatDateTime(details.startTime)} → ${formatTime(details.endTime)}`}
                />
                <InfoRow
                  icon={<DurationIcon fontSize="small" />}
                  label="Duração"
                  value={formatDuration(details.durationMinutes)}
                />
                {details.cancellationReason && (
                  <InfoRow
                    icon={<NotesIcon fontSize="small" />}
                    label="Motivo do cancelamento"
                    value={details.cancellationReason}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* CLIENTE */}
                <SectionHeader icon={<PersonIcon fontSize="small" />} title="Cliente" />
                <InfoRow
                  icon={<PersonIcon fontSize="small" />}
                  label="Nome"
                  value={details.personId ? (
                    <Typography
                      component="span"
                      variant="body2"
                      onClick={() => setPersonOpen(true)}
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {details.clientName}
                    </Typography>
                  ) : details.clientName}
                />
                <InfoRow
                  icon={<PhoneIcon fontSize="small" />}
                  label="Telefone"
                  value={<PhoneDisplay value={details.clientPhone} asLink={true} />}
                />
                <InfoRow
                  icon={<EmailIcon fontSize="small" />}
                  label="E-mail"
                  value={details.clientEmail}
                  href={details.clientEmail ? `mailto:${details.clientEmail}` : null}
                />
                {details.clientNotes && (
                  <Box mb={1}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box sx={{ color: 'text.secondary', mt: 0.2 }}>
                        <NotesIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Observações
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            bgcolor: 'grey.50',
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            mt: 0.5,
                          }}
                        >
                          {details.clientNotes}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* SERVIÇO */}
                {details.service && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <SectionHeader icon={<PriceIcon fontSize="small" />} title="Serviço" />
                    <InfoRow
                      icon={<PriceIcon fontSize="small" />}
                      label="Nome"
                      value={
                        details.service.isDeleted
                          ? `${details.service.name} (removido)`
                          : details.service.name
                      }
                    />
                    {details.service.description && (
                      <InfoRow
                        icon={<NotesIcon fontSize="small" />}
                        label="Descrição"
                        value={details.service.description}
                      />
                    )}
                    <InfoRow
                      icon={<DurationIcon fontSize="small" />}
                      label="Duração padrão"
                      value={formatDuration(details.service.durationMinutes)}
                    />
                    <InfoRow
                      icon={<PriceIcon fontSize="small" />}
                      label="Valor"
                      value={details.service.price != null ? formatCurrency(details.service.price) : null}
                    />
                  </>
                )}
              </Grid>

              {/* ── Coluna Direita ── */}
              <Grid size={{ xs: 12, md: 6 }}>

                {/* PROFISSIONAL */}
                {details.professional && (
                  <>
                    <SectionHeader icon={<ProfessionalIcon fontSize="small" />} title="Profissional" />
                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                      {details.professional.photoId ? (
                        <Box
                          component="img"
                          src={`https://photo.agendario.com.br/${details.professional.photoId}.jpg`}
                          alt={details.professional.name}
                          sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: 'primary.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <ProfessionalIcon color="primary" />
                        </Box>
                      )}
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {details.professional.title
                            ? `${details.professional.title} ${details.professional.name}`
                            : details.professional.name}
                        </Typography>
                        {details.professional.specialty && (
                          <Typography variant="caption" color="text.secondary">
                            {details.professional.specialty}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {details.professional.registrationNumber && (
                      <InfoRow
                        icon={<BadgeIcon fontSize="small" />}
                        label="Registro"
                        value={details.professional.registrationNumber}
                      />
                    )}
                    <InfoRow
                      icon={<PhoneIcon fontSize="small" />}
                      label="Telefone"
                      value={<PhoneDisplay value={details.professional.phone} asLink={true} />}
                    />
                    <InfoRow
                      icon={<EmailIcon fontSize="small" />}
                      label="E-mail"
                      value={details.professional.email}
                      href={details.professional.email ? `mailto:${details.professional.email}` : null}
                    />
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {/* LOCAL */}
                {details.place && (
                  <>
                    <SectionHeader icon={<LocationIcon fontSize="small" />} title="Local" />
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      {details.place.name}
                    </Typography>
                    {fullAddress && (
                      <InfoRow
                        icon={<LocationIcon fontSize="small" />}
                        label="Endereço"
                        value={fullAddress}
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                      />
                    )}
                    <InfoRow
                      icon={<PhoneIcon fontSize="small" />}
                      label="Telefone"
                      value={<PhoneDisplay value={details.place.phone} asLink={true} />}
                    />
                    <InfoRow
                      icon={<EmailIcon fontSize="small" />}
                      label="E-mail"
                      value={details.place.email}
                      href={details.place.email ? `mailto:${details.place.email}` : null}
                    />
                    <InfoRow
                      icon={<WebsiteIcon fontSize="small" />}
                      label="Website"
                      value={details.place.website}
                      href={details.place.website}
                    />
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {/* HISTÓRICO */}
                <SectionHeader icon={<HistoryIcon fontSize="small" />} title="Histórico de Eventos" />
                <HistoryTimeline history={details.history} />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* ── Footer actions ── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Fechar
        </Button>

        {canWrite && details && status === 'pending' && (
          <>
            <Button
              onClick={() => { onConfirm?.(details); onClose(); }}
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckIcon />}
            >
              Confirmar
            </Button>
            <Button
              onClick={() => { onCancel?.(details); onClose(); }}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
            >
              Cancelar
            </Button>
          </>
        )}

        {canWrite && details && status === 'confirmed' && (
          <>
            <Button
              onClick={() => { onMarkAttended?.(details); onClose(); }}
              variant="contained"
              color="info"
              size="small"
              startIcon={<DoneAllIcon />}
            >
              Marcar como Atendido
            </Button>
            <Button
              onClick={() => { onCancel?.(details); onClose(); }}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
            >
              Cancelar
            </Button>
          </>
        )}

        {canWrite && details && status === 'no_show' && (
          <Button
            onClick={() => { onMarkAttended?.(details); onClose(); }}
            variant="contained"
            color="info"
            size="small"
            startIcon={<DoneAllIcon />}
          >
            Marcar como Atendido
          </Button>
        )}

        {canWrite && details && status === 'cancelled' && (
          <Button
            onClick={() => { onReschedule?.(details); onClose(); }}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RescheduleIcon />}
          >
            Reagendar
          </Button>
        )}
      </DialogActions>

      {/* Detalhe da pessoa (modal sobre o modal — fechar volta ao agendamento) */}
      <PersonDetailDialog
        open={personOpen}
        personId={details?.personId}
        onClose={() => setPersonOpen(false)}
      />
    </Dialog>
  );
}
